import { SelectChangeEvent } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useEffect, useMemo } from 'react';

import { DropdownListItem } from '@verantos-dev/shared-components';

import { useAppDispatch, useAppSelector } from '../../lib';
import {
  useGetEncounterStatsDateRangeQuery,
  useGetHolidaysDataQuery,
  useGetMetricBatchTypeListQuery,
} from './api/metricsApiSlice';
import {
  setEncounterStatsBatchTypeId,
  setEncounterStatsEndDate,
  setEncounterStatsStartDate,
} from './state/metricsSlice';
import {
  selectEncounterStatsBatchTypeId,
  selectEncounterStatsEndDate,
  selectEncounterStatsStartDate,
  selectSelectedProjectId,
} from './state/selector';
import { EncounterStatsDateRange } from './types/responseTypes';

type DropdownChangeHandler = (
  event: SelectChangeEvent<unknown> | (Event & { target: { value: unknown; name: string } }),
  child: ReactNode
) => void;

interface UseMetricEncounterStatsResults {
  encounterStatsDateRange: EncounterStatsDateRange | undefined;
  isEncounterStatsDateRangeLoading: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  handleChangeStartDate: (val: Dayjs | null) => void;
  handleChangeEndDate: (val: Dayjs | null) => void;
  batchTypeOptions: DropdownListItem[];
  batchTypeId: string | null;
  isMetricsBatchTypeListLoading: boolean;
  handleBatchTypeChange: DropdownChangeHandler;
  projectId: string | null;
  shouldDisableStartDate: (day: Dayjs) => boolean;
}

export const useMetricEncounterStats = (): UseMetricEncounterStatsResults => {
  const projectId = useAppSelector(selectSelectedProjectId);
  const batchTypeId = useAppSelector(selectEncounterStatsBatchTypeId);
  const startDate = useAppSelector(selectEncounterStatsStartDate);
  const endDate = useAppSelector(selectEncounterStatsEndDate);
  const {
    data: encounterStatsDateRangeData,
    isLoading: isEncounterStatsDateRangeLoading,
    isFetching: isEncounterStatsDateRangeFetching,
  } = useGetEncounterStatsDateRangeQuery(
    {
      projectId: projectId || '',
      batchTypeId: batchTypeId || '',
    },
    {
      skip: !(projectId && batchTypeId),
      refetchOnMountOrArgChange: true,
    }
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (isEncounterStatsDateRangeFetching) return;
    if (!encounterStatsDateRangeData?.data?.minDate || !encounterStatsDateRangeData?.data?.maxDate)
      return;
    dispatch(
      setEncounterStatsStartDate(dayjs(encounterStatsDateRangeData.data?.minDate).toISOString())
    );
    dispatch(
      setEncounterStatsEndDate(dayjs(encounterStatsDateRangeData.data?.maxDate).toISOString())
    );
  }, [
    encounterStatsDateRangeData?.data?.minDate,
    encounterStatsDateRangeData?.data?.maxDate,
    isEncounterStatsDateRangeFetching,
    dispatch,
  ]);

  const handleChangeStartDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setEncounterStatsStartDate(val.toISOString()));
    }
  };
  const handleChangeEndDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setEncounterStatsEndDate(val.toISOString()));
    }
  };

  const {
    data: metricsBatchTypeListData,
    isLoading: isMetricsBatchTypeListLoading,
    isFetching: isMetricsBatchTypeListFetching,
  } = useGetMetricBatchTypeListQuery({ projectId: projectId ?? '' }, { skip: !projectId });

  const batchTypeOptions: DropdownListItem[] = useMemo(() => {
    if (!metricsBatchTypeListData?.data || metricsBatchTypeListData.data.length === 0) {
      return [];
    }
    return metricsBatchTypeListData.data.map((batchType) => ({
      value: String(batchType.id),
      displayValue: batchType.label,
    }));
  }, [metricsBatchTypeListData]);

  const handleBatchTypeChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setEncounterStatsBatchTypeId(value));
    }
  };

  const { data: holidaysData } = useGetHolidaysDataQuery();

  const holidayDates = useMemo<Set<string>>(() => {
    if (!holidaysData?.data) return new Set();
    return new Set(holidaysData.data.map((h) => dayjs(h.date).format('YYYY-MM-DD')));
  }, [holidaysData]);

  const shouldDisableStartDate = (day: Dayjs) => {
    const dow = day.day();
    if (dow === 0 || dow === 6) return true;
    return holidayDates.has(day.format('YYYY-MM-DD'));
  };

  return {
    encounterStatsDateRange: encounterStatsDateRangeData?.data,
    isEncounterStatsDateRangeLoading:
      isEncounterStatsDateRangeLoading || isEncounterStatsDateRangeFetching,
    startDate: startDate ? dayjs(startDate) : null,
    endDate: endDate ? dayjs(endDate) : null,
    handleChangeStartDate,
    handleChangeEndDate,
    batchTypeOptions,
    batchTypeId,
    isMetricsBatchTypeListLoading: isMetricsBatchTypeListFetching || isMetricsBatchTypeListLoading,
    handleBatchTypeChange,
    projectId,
    shouldDisableStartDate,
  };
};
